/**
 * Shipping as checkout shows it, worked out the way the server charges it
 * (server/api-util/checkout-pricing.js serverShippingParams and shippingChoice), so the
 * price on a carrier card is the price the order is charged.
 */
import { calculateBuyerRate } from '../../util/shipping';

/**
 * The handling cost (dollars) added to shipping: each cart line's own handling summed
 * (as the server does), or the listing's for an order without a cart.
 */
export const handlingCostOf = ({ cartItems, listingPublicData, isDomestic }) => {
  const key = isDomestic ? 'domesticHandlingCost' : 'internationalHandlingCost';
  if (Array.isArray(cartItems) && cartItems.length > 0) {
    return cartItems.reduce((sum, item) => sum + (parseFloat(item?.[key]) || 0), 0);
  }
  const params = isDomestic
    ? listingPublicData?.domesticParams
    : listingPublicData?.internationalParams;
  return parseFloat(params?.[key]) || 0;
};

/** Whether the listing's shipping is free (a checkbox's array, or a plain flag), as the server reads it. */
export const isFreeShippingOf = (shippingParams, isDomestic) => {
  const flag = isDomestic
    ? shippingParams?.isFreeShipping
    : shippingParams?.isFreeInternationalShipping;
  return Array.isArray(flag) ? flag.length > 0 : Boolean(flag);
};

/**
 * What the buyer pays for a carrier's rate (dollars, without handling): the server's own
 * price when it sends one, else the same markup worked out here.
 */
export const buyerRateOf = (rate, isDomestic) =>
  rate?.pricing?.buyerRate ?? calculateBuyerRate(rate, isDomestic);

/**
 * The rates the buyer can choose from, cheapest first: only USPS reaches a domestic PO Box.
 */
export const ratesToOffer = (rates, { isPOBox, isInternationalOrder, isDomestic }) => {
  const visible =
    isPOBox && !isInternationalOrder
      ? (rates || []).filter(r => r.carrier === 'USPS')
      : rates || [];
  return [...visible].sort((a, b) => buyerRateOf(a, isDomestic) - buyerRateOf(b, isDomestic));
};

/**
 * One address's identity for its verified (EasyPost) copy: every field the carrier gets,
 * so changing any of them (the phone or state too) verifies it again.
 */
export const addressKeyOf = a =>
  a?.line1
    ? [a.line1, a.line2, a.city, a.state, a.postal_code, a.country, a.phone_number || a.phone]
        .map(v => v || '')
        .join('|')
    : null;

/** A saved address as an order's delivery address. */
export const deliveryAddressOf = saved => ({
  line1: saved.line1,
  line2: saved.line2 || '',
  city: saved.city,
  state: saved.state || '',
  postal_code: saved.postal_code,
  country: saved.country,
  phone_number: saved.phone_number || '',
});
